/**
 * Game state.
 *
 * Plain objects and arrays throughout -- no Map, no Set, no class instances --
 * because this is JSON-serialised onto the wire and into SQLite on every
 * snapshot. Unknowns are represented explicitly (`HandView`, `HandSlot`) so
 * that a client which has only ever seen redacted events can still hold a
 * well-formed state rather than a state with holes in it.
 */

import type { SeatId, TerritoryId } from '../ids.js';
import type { Card } from '../rules/cards.js';
import type { RulesConfig } from '../rules/config.js';

export type Phase =
  | 'lobby'
  | 'setup'
  | 'reinforceTrade'
  | 'reinforcePlace'
  | 'attack'
  | 'occupy'
  | 'fortify'
  | 'cardDraw'
  | 'audit'
  | 'gameOver';

export interface Seat {
  readonly id: SeatId;
  readonly name: string;
  readonly color: string;
  readonly isHost: boolean;
  /** The two-player neutral army: owns ground, never takes a turn. */
  readonly isNeutral: boolean;
  readonly eliminated: boolean;
  readonly eliminatedBy: SeatId | null;
}

export interface TerritoryState {
  readonly owner: SeatId | null;
  readonly armies: number;
}

/** A card in a hand, which may not have been named yet. */
export type HandSlot =
  | { readonly kind: 'known'; readonly card: Card }
  | { readonly kind: 'unknown' };

/**
 * What a given viewer can see of a hand. Own hand: the slots. Anyone else's:
 * only how many cards they hold, which is public at a real table anyway.
 */
export type HandView =
  | { readonly visible: true; readonly slots: readonly HandSlot[] }
  | { readonly visible: false; readonly count: number };

export function handSize(hand: HandView): number {
  return hand.visible ? hand.slots.length : hand.count;
}

/** An attack awaiting the defender's dice choice, then the roll. */
export interface PendingBattle {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly attacker: SeatId;
  readonly defender: SeatId;
  readonly attackerDice: number;
  /** `null` until the defender has chosen. */
  readonly defenderDice: number | null;
}

/**
 * A conquest awaiting armies to advance. Blocks every other action: this is
 * the moment where the app and the physical board most easily drift apart.
 */
export interface PendingOccupation {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly minimumArmies: number;
  readonly maximumArmies: number;
}

export interface TurnState {
  readonly seat: SeatId;
  readonly number: number;
  readonly armiesToPlace: number;
  readonly conqueredThisTurn: boolean;
  readonly fortifiesUsed: number;
  readonly pendingBattle: PendingBattle | null;
  readonly pendingOccupation: PendingOccupation | null;
}

/**
 * Something incoherent in the folded state. The app warns rather than blocks,
 * and a correction to a past event can legitimately make later events
 * nonsense, so the reducer records problems instead of throwing.
 */
export interface Anomaly {
  readonly code: string;
  readonly message: string;
  readonly subject?: string;
}

export interface GameState {
  readonly status: 'lobby' | 'setup' | 'playing' | 'gameOver';
  readonly phase: Phase;
  readonly rules: RulesConfig | null;
  readonly mapId: string | null;
  readonly seats: readonly Seat[];
  readonly seatOrder: readonly SeatId[];
  readonly territories: Readonly<Record<TerritoryId, TerritoryState>>;
  readonly hands: Readonly<Record<SeatId, HandView>>;
  /** Sets traded by everyone, which drives the escalating ladder. */
  readonly setsTraded: number;
  readonly turn: TurnState | null;
  readonly winner: SeatId | null;
  readonly anomalies: readonly Anomaly[];
  readonly lastSeq: number;
}

export const EMPTY_STATE: GameState = Object.freeze({
  status: 'lobby',
  phase: 'lobby',
  rules: null,
  mapId: null,
  seats: [],
  seatOrder: [],
  territories: {},
  hands: {},
  setsTraded: 0,
  turn: null,
  winner: null,
  anomalies: [],
  lastSeq: 0,
});

export function seatById(state: GameState, id: SeatId): Seat | undefined {
  return state.seats.find((seat) => seat.id === id);
}

/** Owner lookup in the shape the map-graph helpers expect. */
export function ownerMap(state: GameState): ReadonlyMap<TerritoryId, SeatId | null> {
  const owners = new Map<TerritoryId, SeatId | null>();
  for (const [id, territory] of Object.entries(state.territories)) {
    owners.set(id, territory.owner);
  }
  return owners;
}

export function territoriesOwnedBy(state: GameState, seat: SeatId): readonly TerritoryId[] {
  return Object.entries(state.territories)
    .filter(([, territory]) => territory.owner === seat)
    .map(([id]) => id);
}
